import { useState, useCallback } from 'react';
import { comensalApi } from '../api/comensalApi';
import { showErrorToast, showSuccessToast } from '../utils/errorHandler';

/**
 * Hook personalizado para gestión de comensales
 * Proporciona estado y funcion para operaciones CRUD de comensales
 * Proporciona estado y funciones para operaciones CRUD de comensales
 */
export const useComensales = () => {
  const [comensales, setComensales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Obtiene comensales con filtros opcionales
   */
  const fetchComensales = useCallback(async (idProyecto = null, numDoc = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comensalApi.getAll(idProyecto, numDoc);
      setComensales(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar comensales');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene un comensal específico por ID
   */
  const fetchComensalById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comensalApi.getById(id);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar el comensal');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crea un nuevo comensal
   */
  const createComensal = useCallback(async (data, showToast = true) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comensalApi.create(data);
      
      if (showToast) {
        showSuccessToast('Comensal creado exitosamente');
        // Refrescar la lista de comensales solo si se muestra toast (interfaz normal)
        await fetchComensales();
      }
      
      return response.data;
    } catch (err) {
      if (showToast) {
        const errorMessage = showErrorToast(err, 'Error al crear el comensal');
        setError(errorMessage);
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchComensales]);

  /**
   * Actualiza un comensal existente
   */
  const updateComensal = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comensalApi.update(id, data);
      showSuccessToast('Comensal actualizado exitosamente');
      
      // Refrescar la lista de comensales
      await fetchComensales();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al actualizar el comensal');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchComensales]);

  /**
   * Elimina un comensal
   */
  const deleteComensal = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await comensalApi.delete(id);
      showSuccessToast('Comensal eliminado exitosamente');
      
      // Refrescar la lista de comensales
      await fetchComensales();
      
      return true;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al eliminar el comensal');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchComensales]);

  /**
   * Obtiene comensales por proyecto
   */
  const fetchComensalesByProyecto = useCallback(async (proyectoId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comensalApi.getByProyecto(proyectoId);
      console.log('🔍 DEBUG useComensales - Response completa:', response);
      console.log('🔍 DEBUG useComensales - Response.data:', response.data);
      console.log('🔍 DEBUG useComensales - Tipo de response.data:', Array.isArray(response.data) ? 'Array' : typeof response.data);
      
      // Verificar si response.data es un array directo o está envuelto
      let comensales = response.data;
      if (comensales && !Array.isArray(comensales) && comensales.data) {
        comensales = comensales.data;
      }
      
      // Log de los primeros 3 comensales para verificar el campo activo
      if (Array.isArray(comensales) && comensales.length > 0) {
        console.log('🔍 DEBUG useComensales - Primeros 3 comensales (campo activo):', 
          comensales.slice(0, 3).map(c => ({
            id: c.id,
            nombre: c.nombre,
            activo: c.activo,
            activoType: typeof c.activo,
            activoValue: JSON.stringify(c.activo)
          }))
        );
      }
      
      return comensales || [];
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar comensales del proyecto');
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Busca comensales por número de documento
   * Devuelve el primer comensal encontrado o null si no hay resultados
   */
  const fetchComensalByNumDoc = useCallback(async (numDoc, proyectoId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comensalApi.getByNumDoc(numDoc, proyectoId);
      const comensales = response.data;
      
      // El backend ahora filtra correctamente y devuelve solo los comensales que coinciden
      // Si hay resultados, devolver el primer comensal encontrado
      if (Array.isArray(comensales) && comensales.length > 0) {
        return comensales[0];
      }
      
      // Si no hay resultados o el array está vacío
      return null;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al buscar comensal por documento');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cambia el estado activo de un comensal
   */
  const toggleActivoComensal = useCallback(async (id, activo = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comensalApi.toggleActivo(id, activo);
      
      if (response.data.success) {
        const mensaje = response.data.message || 
          (response.data.activo ? 'Comensal activado' : 'Comensal desactivado');
        showSuccessToast(mensaje);
        
        // Actualizar el estado local si existe
        setComensales(prev => prev.map(comensal => 
          comensal.id === id 
            ? { ...comensal, activo: response.data.activo }
            : comensal
        ));
        
        return response.data;
      } else {
        throw new Error(response.data.message || 'Error al cambiar estado del comensal');
      }
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cambiar estado del comensal');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene dietas activas de comensales filtradas por proyecto
   * @param {Object} filtros - Filtros para la consulta
   * @param {number} filtros.id_proyecto - ID del proyecto (obligatorio)
   */
  const fetchDietasActivas = useCallback(async (filtros = {}) => {
    try {
      const response = await comensalApi.getDietasActivas(filtros);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar dietas activas');
      setError(errorMessage);
      throw err;
    }
  }, []);


  // DESHABILITADO: No cargar comensales automáticamente para evitar requests concurrentes
  // Los componentes que necesiten datos deben llamar fetchComensales() manualmente
  // useEffect(() => {
  //   fetchComensales();
  // }, [fetchComensales]);

  return {
    // Estado
    comensales,
    loading,
    error,
    
    // Funciones
    fetchComensales,
    fetchComensalById,
    createComensal,
    updateComensal,
    deleteComensal,
    fetchComensalesByProyecto,
    fetchComensalByNumDoc,
    toggleActivoComensal,
    fetchDietasActivas,
    
    // Función para limpiar errores
    clearError: () => setError(null),
  };
};

export default useComensales;
